/* eslint-disable complexity */
import { Injectable } from '@angular/core';
import { LegView } from '@sequence/models/view/leg.view';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { AlertService } from '@shared/services/alert/alert.service';
import { Observable, map, of } from 'rxjs';
import { LoadService } from './load.service';
import { LoadRequest } from './models/load.request';
import { CabinClassCount, JumpseatCount, LoadView } from './models/load.view';
import { DateTime } from 'luxon';
import { DataItem } from '@shared/models/data-item';

@Injectable({
  providedIn: 'root',
})
export class LoadStore {
  constructor(
    private loadService: LoadService,
    private alertService: AlertService
  ) {}

  getLoad(
    legView: LegView,
    isRefresh = false
  ): Observable<RequestState<LoadView>> {
    const request = legView.getStandbyListRequest() as LoadRequest;

    if (!request) {
      return of(new RequestState<LoadView>(RequestStatus.IsCompleteNoResponse));
    }

    return this.loadService.getLoad(request, isRefresh).pipe(
      map((responseState) => {
        if (responseState.hasError) {
          this.showErrorAlert();
        }

        if (!responseState.response) {
          return responseState.into<LoadView>();
        }

        const loadView = this.parseLoad(
          responseState.response?.raw,
          responseState.response?.timestamp
        );

        return responseState.into<LoadView>(loadView);
      })
    );
  }

  private showErrorAlert(): void {
    this.alertService.setAlert({
      type: 'error',
      message: 'Unable to get load data.',
    });
  }

  private parseLoad(text: string, timestamp: string): LoadView {
    const classes = this.parseCabinClasses(text);
    const auth = this.parseAuth(text, classes);
    const booked = this.parseBooked(text, classes);
    const avail = this.calculateAvail(auth, booked);
    const jumpseats = this.parseJumpseats(text);
    const onBoard = this.parseBoardingProgress(text);

    return {
      timestamp: DateTime.fromISO(timestamp, { zone: 'utc' }),
      text,
      auth,
      booked,
      avail,
      jumpseats,
      onBoard,
      jumpseatDataItems: this.createJsDataItems(
        jumpseats.flightDeck,
        jumpseats.cabin
      ),
    };
  }

  private parseCabinClasses(text: string): string[] {
    const lines = text.split('\n');

    // All aircraft have at least C and Y cabin classes.
    // const classesPattern = /^\s+(?<F>F)?\s+(?<C>C)\s+(?<W>W)?\s+(?<Y>Y)/;
    const classesPattern = /^\s+(F)?\s+(C)\s+(W)?\s+(Y)/;

    // Figure out which cabin classes are used.
    for (const line of lines) {
      const match = classesPattern.exec(line);
      if (match) {
        // Return array of matched items. Ignore first item which is whole line.
        return match.filter((item, index) => index > 0 && item !== undefined);
      }
    }
    return [];
  }

  private parseAuth(text: string, classes: string[]): CabinClassCount {
    const pattern = /AUTH\s+(\d+)\s+(\d+)\s+(\d+)?\s+(\d+)?/;
    const match = pattern.exec(text);
    const filtered = match?.filter(
      (item, index) => index > 0 && item !== undefined
    );
    return this.mapCabinClassCount(classes, filtered || []);
  }

  private parseBooked(text: string, classes: string[]): CabinClassCount {
    const pattern = /BOOKED\s+(\d+)\s+(\d+)\s+(\d+)?\s+(\d+)?/;
    const match = pattern.exec(text);
    const filtered = match?.filter(
      (item, index) => index > 0 && item !== undefined
    );
    return this.mapCabinClassCount(classes, filtered || []);
  }

  /**
   * Cabin classes for aircraft types:
   * 777-300 F - First C - Business W - Prem Econ Y - Main
   * 777/787           C - Business W - Prem Econ Y - Main
   * 320/737           C - First                  Y - Main
   * 321T    F - First C - Business               Y - Main
   */
  private mapCabinClassCount(
    classes: string[],
    counts: string[]
  ): CabinClassCount {
    if (classes.length === 0) {
      return { main: 0, total: 0 };
    }

    const cabinClassCount: CabinClassCount = {
      main: 0,
      total: 0,
    };

    if (classes[0] === 'F' && classes.length === 4) {
      cabinClassCount.first = parseInt(counts[0]);
      cabinClassCount.business = parseInt(counts[1]);
      cabinClassCount.premEcon = parseInt(counts[2]);
      cabinClassCount.main = parseInt(counts[3]);
    } else if (classes[0] === 'F' && classes.length === 3) {
      cabinClassCount.first = parseInt(counts[0]);
      cabinClassCount.business = parseInt(counts[1]);
      cabinClassCount.main = parseInt(counts[2]);
    } else if (classes.length === 3) {
      cabinClassCount.business = parseInt(counts[0]);
      cabinClassCount.premEcon = parseInt(counts[1]);
      cabinClassCount.main = parseInt(counts[2]);
    } else if (classes.length === 2) {
      cabinClassCount.first = parseInt(counts[0]);
      cabinClassCount.main = parseInt(counts[1]);
    }

    // Calculate total
    const total = counts.reduce((accum, item) => accum + parseInt(item), 0);
    cabinClassCount.total = total;

    return cabinClassCount;
  }

  private calculateAvail(
    auth: CabinClassCount,
    booked: CabinClassCount
  ): CabinClassCount {
    const avail: CabinClassCount = { main: 0, total: 0 };

    if (auth.first && booked.first) {
      avail.first = auth.first - booked.first;
    }

    if (auth.business && booked.business) {
      avail.business = auth.business - booked.business;
    }

    if (auth.premEcon && booked.premEcon) {
      avail.premEcon = auth.premEcon - booked.premEcon;
    }

    if (auth.main && booked.main) {
      avail.main = auth.main - booked.main;
    }

    if (auth.total && booked.total) {
      avail.total = auth.total - booked.total;
    }

    return avail;
  }

  private parseJumpseats(text: string): {
    cabin: JumpseatCount;
    flightDeck: JumpseatCount;
  } {
    const actualPattern = /ACTUAL\s+W-(?<fd>\d+)\s+X-(?<cabin>\d+)/;
    const inUsePattern = /IN USE\s+W-(?<fd>\d+)?\s+X-(?<cabin>\d+)?/;

    const actualMatch = text.match(actualPattern)?.groups;
    const inUseMatch = text.match(inUsePattern)?.groups;

    return {
      cabin: {
        actual: parseInt(actualMatch?.['cabin'] || '0'),
        inUse: parseInt(inUseMatch?.['cabin'] || '0'),
      },
      flightDeck: {
        actual: parseInt(actualMatch?.['fd'] || '0'),
        inUse: parseInt(inUseMatch?.['fd'] || '0'),
      },
    };
  }

  /** Parses LCL ET (tickets scanned) and TLOB (Total) to determine boarding progress. */
  private parseBoardingProgress(text: string): {
    total: number;
    current: number;
  } {
    const tlobPattern = /TLOB\s+(\d+)\s+(\d+)\s*(\d+)?\s*(\d+)?/;
    const lclEtPattern = /LCL ET\s+(\d+)\s+(\d+)\s*(\d+)?\s*(\d+)?/;

    const tlobTotal = tlobPattern
      .exec(text)
      ?.filter((item, index) => index > 0 && item !== undefined)
      .reduce((accum, item) => accum + parseInt(item), 0);

    const lclEtTotal = lclEtPattern
      .exec(text)
      ?.filter((item, index) => index > 0 && item !== undefined)
      .reduce((accum, item) => accum + parseInt(item), 0);

    return {
      total: tlobTotal || 0,
      current: lclEtTotal || 0,
    };
  }

  private createJsDataItems(
    fd: JumpseatCount,
    cabin: JumpseatCount
  ): DataItem[] {
    const dataItems: DataItem[] = [];

    dataItems.push({
      label: 'Flight Deck',
      value: `${fd.inUse}/${fd.actual}`,
      style: fd.inUse > 0 ? 'caution' : 'success',
    });

    dataItems.push({
      label: 'Cabin',
      value: `${cabin.inUse}/${cabin.actual}`,
    });

    return dataItems;
  }
}
